/**
 * Dashboard API Service
 * Centralized API abstraction for dashboard widgets
 * Provides reusable methods for fetching dashboard data
 */

import { api } from '@/lib/api/client';
import { UserManagementData, SharedStream } from '@/lib/shared-types';

// Re-export SharedStream for components that import from this module
export type { SharedStream };

// Types for dashboard-specific data
export interface ActivityData {
  id: number;
  eventType: string;
  userId: number | null;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  action: string;
  details: string;
  timestamp: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface RecordingData {
  id: string;
  streamId: number;
  streamTitle: string;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  creator?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}


export interface CommentModerationData {
  id: number;
  content: string;
  author: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  stream?: {
    id: number;
    title: string;
  };
  createdAt: string;
  status: 'pending' | 'approved' | 'rejected';
  likes: number;
  reports: number;
}

export class DashboardApiService {
  /**
   * Get recent users for admin management
   */
  async getRecentUsers(limit = 5): Promise<UserManagementData[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
      });

      const response = await api.get<{
        users: UserManagementData[];
        total: number;
      }>(`/admin/users?${params}`);

      if (response.success && response.data) {
        return response.data.users || [];
      }

      console.warn('Failed to fetch recent users:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching recent users:', error);
      return [];
    }
  }

  /**
   * Get recent user activities from audit logs
   */
  async getRecentActivities(limit = 5): Promise<ActivityData[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
      });

      const response = await api.get<{
        logs: ActivityData[];
        total: number;
      }>(`/admin/audit-logs/recent?${params}`);

      if (response.success && response.data) {
        return response.data.logs || [];
      }

      console.warn('Failed to fetch recent activities:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  }

  /**
   * Get recent streams (for admin or user)
   */
  async getRecentStreams(limit = 5, admin = false): Promise<SharedStream[]> {
    try {
      const endpoint = admin ? '/admin/streams' : '/streams';
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
        status: 'all', // Get all statuses for recent view
      });

      const response = await api.get<{
        streams: SharedStream[];
        total: number;
      }>(`${endpoint}?${params}`);

      console.log(`[DashboardAPI] getRecentStreams response:`, {
        success: response.success,
        streamsCount: response.data?.streams?.length || 0,
        streams: response.data?.streams?.map(s => ({ id: s.id, title: s.title })) || [],
        error: response.error
      });

      if (response.success && response.data) {
        return response.data.streams || [];
      }

      console.warn('Failed to fetch recent streams:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching recent streams:', error);
      return [];
    }
  }

  /**
   * Get live streams for monitoring
   */
  async getLiveStreams(limit = 10): Promise<SharedStream[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
        status: 'live',
      });

      const response = await api.get<{
        streams: SharedStream[];
        total: number;
      }>(`/streams?${params}`);

      if (response.success && response.data) {
        return response.data.streams || [];
      }

      console.warn('Failed to fetch live streams:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching live streams:', error);
      return [];
    }
  }

  /**
   * Get stream analytics (viewer count, etc.)
   */
  async getStreamAnalytics(streamId: number): Promise<{
    viewerCount: number;
    isLive: boolean;
    analytics?: {
      liveViewers: number;
      totalViews?: number;
      peakConcurrentViewers?: number;
    };
  } | null> {
    try {
      const response = await api.get<{
        viewerCount: number;
        isLive: boolean;
        analytics?: {
          liveViewers: number;
          totalViews?: number;
          peakConcurrentViewers?: number;
        };
      }>(`/streams/${streamId}/analytics`);

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching stream analytics:', error);
      return null;
    }
  }

  /**
   * Get pending comments for moderation
   */
  async getPendingComments(limit = 5): Promise<CommentModerationData[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
        moderated: 'false',
      });

      const response = await api.get<{
        comments: CommentModerationData[];
        total: number;
      }>(`/moderator/comments?${params}`);

      if (response.success && response.data) {
        return response.data.comments || [];
      }

      console.warn('Failed to fetch pending comments:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching pending comments:', error);
      return [];
    }
  }


  /**
   * Get recent recordings
   */
  async getRecentRecordings(limit = 5): Promise<RecordingData[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
      });

      const response = await api.get<{
        recordings: RecordingData[];
        total: number;
      }>(`/recordings?${params}`);

      if (response.success && response.data) {
        return response.data.recordings || [];
      }

      console.warn('Failed to fetch recent recordings:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching recent recordings:', error);
      return [];
    }
  }

  /**
   * Get user's accessible streams
   */
  async getUserStreams(limit = 5): Promise<SharedStream[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: '0',
      });

      const response = await api.get<{
        streams: SharedStream[];
        total: number;
      }>(`/streams?${params}`);

      if (response.success && response.data) {
        return response.data.streams || [];
      }

      console.warn('Failed to fetch user streams:', response.error);
      return [];
    } catch (error) {
      console.error('Error fetching user streams:', error);
      return [];
    }
  }

  /**
   * Moderate a comment (approve/reject)
   */
  async moderateComment(commentId: number, action: 'approve' | 'reject', reason?: string): Promise<boolean> {
    try {
      const response = await api.put<{
        comment: CommentModerationData;
        message: string;
      }>(`/moderator/comments/${commentId}/moderate`, {
        action,
        reason,
      });

      return response.success;
    } catch (error) {
      console.error('Error moderating comment:', error);
      return false;
    }
  }

  /**
   * Update stream status (start/stop)
   */
  async updateStreamStatus(streamId: number, action: 'start' | 'stop'): Promise<boolean> {
    try {
      const status = action === 'start' ? 'live' : 'ended';
      const response = await api.put<{
        stream: SharedStream;
        message: string;
      }>(`/streams/${streamId}`, {
        status,
      });

      return response.success;
    } catch (error) {
      console.error('Error updating stream status:', error);
      return false;
    }
  }

  /**
   * Download recording
   */
  async downloadRecording(streamId: number): Promise<string | null> {
    try {
      const response = await api.get<{
        downloadUrl: string;
      }>(`/streams/${streamId}/recording/download`);

      if (response.success && response.data) {
        return response.data.downloadUrl;
      }

      return null;
    } catch (error) {
      console.error('Error getting recording download URL:', error);
      return null;
    }
  }

  /**
   * Get playback URL for a recording's source stream
   */
  async getRecordingPlaybackUrl(streamId: number): Promise<string | null> {
    try {
      const response = await api.get<{
        playbackUrl: string;
      }>(`/streams/${streamId}/playback`);

      if (response.success && response.data) {
        return response.data.playbackUrl;
      }

      return null;
    } catch (error) {
      console.error('Error getting recording playback URL:', error);
      return null;
    }
  }

  /**
   * Join a stream (get stream access URL)
   */
  async joinStream(streamId: number): Promise<string | null> {
    try {
      const response = await api.get<{
        playbackUrl: string;
        expiresAt: string;
        streamId: number;
        status: string;
      }>(`/streams/${streamId}/playback`);

      if (response.success && response.data) {
        return response.data.playbackUrl;
      }

      return null;
    } catch (error) {
      console.error('Error joining stream:', error);
      return null;
    }
  }

  /**
   * Connect to realtime analytics SSE stream
   */
  connectRealtimeAnalytics(
    onMessage: (data: RealtimeAnalyticsData) => void,
    streamId?: number,
    onError?: (error: Event) => void
  ): { disconnect: () => void } {
    const url = new URL('/api/admin/analytics/realtime', window.location.origin);
    if (streamId) {
      url.searchParams.set('streamId', String(streamId));
    }

    const eventSource = new EventSource(url.toString());

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as RealtimeAnalyticsData;
        onMessage(data);
      } catch (error) {
        console.error('Error parsing realtime analytics message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Realtime analytics SSE error:', error);
      onError?.(error);
    };

    return {
      disconnect: () => {
        eventSource.close();
      },
    };
  }
}

export interface RealtimeAnalyticsData {
  type: string;
  timestamp: string;
  metrics: Array<{
    streamId: number;
    currentViewers: number;
    peakViewers: number;
    totalViews: number;
    totalPlays: number;
    joins: number;
    leaves: number;
    lastUpdate: string;
    engagement: {
      comments: number;
      likes: number;
      reactions: number;
    };
  }>;
  liveStreams: Array<{
    id: number;
    title: string;
    status: string;
    viewerCount: number;
    startedAt: string;
  }>;
  note?: string;
}

// Export singleton instance
export const dashboardApi = new DashboardApiService();
